import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../services/api';
import { useAuth0 } from '@auth0/auth0-react';
import { Pool } from '../../types/pool';

interface LinkedPool {
  id: string;
  name: string;
  type: string;
  hasBracketMatchups: boolean;
}

interface BracketTeam {
  id: string;
  name: string;
  shortName: string;
  seed?: number;
  region?: string;
}

interface PlayInGame {
  region: string;
  seed: number;
  team1: BracketTeam;
  team2: BracketTeam;
  resolved: boolean;
  winnerId: string | null;
}

interface PlayInStatus {
  playInGames: PlayInGame[];
  totalTeams: number;
  playInCount: number;
  readyForBracket: boolean;
}

interface BracketMatchup {
  id: string;
  round: string;
  team1?: BracketTeam;
  team2?: BracketTeam;
  winner?: { id: string; name: string };
  homeScore?: number;
  awayScore?: number;
  isFinal: boolean;
  scheduledAt: string;
}

interface RoundMatchups {
  [round: string]: BracketMatchup[];
}

const ROUND_ORDER = [
  'Round of 64',
  'Round of 32',
  'Sweet 16',
  'Elite 8',
  'Final Four',
  'Championship',
];

export function AdminBracket() {
  // If poolId is provided in URL, use it (for backward compatibility with per-pool bracket route)
  // Otherwise, fetch the master bracket pool
  const { poolId: urlPoolId } = useParams<{ poolId?: string }>();
  const { getAccessTokenSilently } = useAuth0();
  const [poolId, setPoolId] = useState<string | null>(urlPoolId || null);
  const [pool, setPool] = useState<Pool | null>(null);
  const [linkedPools, setLinkedPools] = useState<LinkedPool[]>([]);
  const [matchups, setMatchups] = useState<RoundMatchups>({});
  const [playInStatus, setPlayInStatus] = useState<PlayInStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [resolvingPlayIn, setResolvingPlayIn] = useState<string | null>(null);
  const [submittingResult, setSubmittingResult] = useState<string | null>(null);
  const [correctingResult, setCorrectingResult] = useState<string | null>(null); // eventId being corrected
  const [syncing, setSyncing] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadBracketData();
  }, []);

  const loadBracketData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();

      // If no poolId provided in URL, fetch master bracket
      let finalPoolId = poolId;
      if (!finalPoolId) {
        const masterData = await api.getMasterBracket(token);
        finalPoolId = masterData.masterPoolId;
        setLinkedPools(masterData.linkedPools);
      }

      setPoolId(finalPoolId);

      if (!finalPoolId) {
        throw new Error('No pool ID available');
      }

      const [poolData, matchupData, playInData] = await Promise.all([
        api.getPool(finalPoolId),
        api.getBracketMatchups(finalPoolId, token).catch(() => ({ matchups: {} })),
        api.getPlayInGames(finalPoolId, token).catch(() => null),
      ]);
      setPool(poolData);
      setMatchups(matchupData.matchups || {});
      if (playInData) {
        setPlayInStatus(playInData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bracket');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateMatchups = async () => {
    if (!poolId) return;
    try {
      setGenerating(true);
      setError(null);
      const token = await getAccessTokenSilently();
      await api.generateBracketMatchups(poolId, token);
      setSuccessMessage('Bracket matchups generated successfully');
      await loadBracketData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate matchups');
    } finally {
      setGenerating(false);
    }
  };

  const handleResolvePlayInGame = async (game: PlayInGame, winnerId: string) => {
    if (!poolId) return;
    const loserId = winnerId === game.team1.id ? game.team2.id : game.team1.id;

    try {
      setResolvingPlayIn(winnerId);
      setError(null);
      const token = await getAccessTokenSilently();
      await api.resolvePlayInGame(poolId, { winnerId, loserId }, token);
      setSuccessMessage(`${winnerId === game.team1.id ? game.team1.name : game.team2.name} advances from play-in!`);
      await loadBracketData();
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resolve play-in game');
    } finally {
      setResolvingPlayIn(null);
    }
  };

  const handleTeamClick = async (matchup: BracketMatchup, teamId: string, teamName: string) => {
    if (!poolId || matchup.isFinal) return;

    try {
      setSubmittingResult(matchup.id);
      setError(null);
      const token = await getAccessTokenSilently();
      await api.submitBracketResult(
        poolId,
        {
          eventId: matchup.id,
          winnerId: teamId,
          homeScore: matchup.homeScore || 0,
          awayScore: matchup.awayScore || 0,
        },
        token
      );
      setSuccessMessage(`${teamName} advanced!`);
      await loadBracketData();
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit result');
    } finally {
      setSubmittingResult(null);
    }
  };

  const handleCorrectResult = async (matchup: BracketMatchup, newWinnerId: string) => {
    if (!poolId) return;
    if (!window.confirm(
      `Are you sure you want to correct this result? This will reverse the current winner and process the new winner across all NCAA pools.`
    )) return;

    try {
      setCorrectingResult(matchup.id);
      setError(null);
      const token = await getAccessTokenSilently();
      const result = await api.correctBracketResult(
        poolId,
        { eventId: matchup.id, newWinnerId },
        token,
      );
      setSuccessMessage(result.message || 'Result corrected successfully');
      setCorrectingResult(null);
      await loadBracketData();
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to correct result');
      setCorrectingResult(null);
    }
  };

  const handleResetResult = async (matchup: BracketMatchup) => {
    if (!poolId) return;
    if (!window.confirm(
      `Reset this matchup? The result will be cleared and scoring reversed. You can then select the correct winner.`
    )) return;

    try {
      setCorrectingResult(matchup.id);
      setError(null);
      const token = await getAccessTokenSilently();
      const result = await api.correctBracketResult(
        poolId,
        { eventId: matchup.id },
        token,
      );
      setSuccessMessage(result.message || 'Result reset. Select the correct winner.');
      setCorrectingResult(null);
      await loadBracketData();
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset result');
      setCorrectingResult(null);
    }
  };

  const handleSyncAdvancement = async () => {
    if (!poolId) return;
    try {
      setSyncing(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const result = await api.syncBracketAdvancement(poolId, token);
      setSuccessMessage(result.message || 'Bracket advancement synced');
      await loadBracketData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync bracket advancement');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading bracket...</p>
        </div>
      </div>
    );
  }

  if (!pool) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Pool not found</p>
      </div>
    );
  }

  const hasMatchups = Object.keys(matchups).length > 0;
  const roundsWithMatchups = ROUND_ORDER.filter(r => matchups[r]?.length > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Tournament Bracket</h1>
        <p className="text-gray-600 mt-2">{pool?.name}</p>
      </div>

      {/* Linked Pools Banner */}
      {linkedPools.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-900 font-medium mb-2">
            Results entered here update all NCAA pools:
          </p>
          <div className="flex flex-wrap gap-2">
            {linkedPools.map((p) => (
              <span
                key={p.id}
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  p.hasBracketMatchups
                    ? 'bg-blue-200 text-blue-800'
                    : 'bg-blue-100 text-blue-700'
                }`}
              >
                {p.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Status Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800">
          {successMessage}
        </div>
      )}

      {/* Play-in Games Section */}
      {playInStatus !== null && playInStatus.playInCount > 0 && (
        <div className="bg-white rounded-lg border border-amber-300 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Play-in Games ({playInStatus.playInCount} pairs)</h2>
          <p className="text-gray-600 mb-4">
            {playInStatus.readyForBracket
              ? 'All play-in games resolved! Ready to generate bracket.'
              : `Pool has ${playInStatus.totalTeams} teams. Resolve the ${playInStatus.playInCount} play-in games below to proceed.`}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {playInStatus.playInGames.map((game, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="text-center mb-3">
                  <span className="text-sm font-semibold text-gray-700">
                    {game.region} Region • #{game.seed}
                  </span>
                </div>
                <div className="space-y-2">
                  <button
                    onClick={() => handleResolvePlayInGame(game, game.team1.id)}
                    disabled={resolvingPlayIn === game.team1.id}
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded hover:bg-blue-50 hover:border-blue-400 transition-colors disabled:opacity-50 disabled:cursor-wait text-left"
                  >
                    <span className="font-medium text-gray-900">{game.team1.name}</span>
                    <span className="text-xs text-gray-500 ml-2">({game.team1.shortName})</span>
                  </button>
                  <div className="text-center text-sm text-gray-500">vs</div>
                  <button
                    onClick={() => handleResolvePlayInGame(game, game.team2.id)}
                    disabled={resolvingPlayIn === game.team2.id}
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded hover:bg-blue-50 hover:border-blue-400 transition-colors disabled:opacity-50 disabled:cursor-wait text-left"
                  >
                    <span className="font-medium text-gray-900">{game.team2.name}</span>
                    <span className="text-xs text-gray-500 ml-2">({game.team2.shortName})</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        {!hasMatchups && (
          <button
            onClick={handleGenerateMatchups}
            disabled={generating || (playInStatus !== null && !playInStatus.readyForBracket)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? 'Generating...' : 'Generate Bracket Matchups'}
          </button>
        )}
        {hasMatchups && (
          <button
            onClick={handleSyncAdvancement}
            disabled={syncing}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {syncing ? 'Syncing...' : 'Sync Bracket Advancement'}
          </button>
        )}
      </div>

      {!hasMatchups && playInStatus !== null && !playInStatus.readyForBracket && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
          Resolve all play-in games before generating the bracket
        </div>
      )}

      {/* Bracket Display */}
      {hasMatchups ? (
        <div className="space-y-8">
          {roundsWithMatchups.map(round => (
            <div key={round} className="bg-white rounded-lg border border-gray-200 p-6">
              {/* Round Header */}
              <h2 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b border-gray-200">
                {round}
              </h2>

              {/* Matchups Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {matchups[round].map((matchup, idx) => (
                  <div
                    key={matchup.id}
                    className="border border-gray-300 rounded-lg overflow-hidden bg-gray-50"
                  >
                    {/* Matchup Header */}
                    <div className="bg-gray-100 px-4 py-2 border-b border-gray-300">
                      <p className="text-sm text-gray-600 font-medium">Matchup {idx + 1}</p>
                      {matchup.scheduledAt && (
                        <p className="text-xs text-gray-500">
                          {new Date(matchup.scheduledAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>

                    {/* Teams */}
                    <div className="p-4 space-y-2">
                      {/* Team 1 */}
                      <TeamSlot
                        team={matchup.team1}
                        score={matchup.homeScore}
                        isWinner={matchup.winner?.id === matchup.team1?.id}
                        isFinal={matchup.isFinal}
                        isEliminated={
                          matchup.isFinal && matchup.winner?.id !== matchup.team1?.id
                        }
                        onClick={() => {
                          if (matchup.isFinal && matchup.winner?.id !== matchup.team1?.id && matchup.team1) {
                            // Clicking the loser on a completed matchup = correct result
                            handleCorrectResult(matchup, matchup.team1.id);
                          } else if (!matchup.isFinal && matchup.team1) {
                            handleTeamClick(matchup, matchup.team1.id, matchup.team1.name);
                          }
                        }}
                        isLoading={submittingResult === matchup.id || correctingResult === matchup.id}
                      />

                      {/* Divider */}
                      <div className="my-1 border-t border-gray-300"></div>

                      {/* Team 2 */}
                      <TeamSlot
                        team={matchup.team2}
                        score={matchup.awayScore}
                        isWinner={matchup.winner?.id === matchup.team2?.id}
                        isFinal={matchup.isFinal}
                        isEliminated={
                          matchup.isFinal && matchup.winner?.id !== matchup.team2?.id
                        }
                        onClick={() => {
                          if (matchup.isFinal && matchup.winner?.id !== matchup.team2?.id && matchup.team2) {
                            handleCorrectResult(matchup, matchup.team2.id);
                          } else if (!matchup.isFinal && matchup.team2) {
                            handleTeamClick(matchup, matchup.team2.id, matchup.team2.name);
                          }
                        }}
                        isLoading={submittingResult === matchup.id || correctingResult === matchup.id}
                      />

                      {/* Correct Result button for completed matchups */}
                      {matchup.isFinal && (
                        <button
                          onClick={() => handleResetResult(matchup)}
                          disabled={correctingResult === matchup.id}
                          className="w-full mt-1 text-xs text-gray-400 hover:text-red-600 py-1 transition-colors disabled:opacity-50"
                        >
                          {correctingResult === matchup.id ? 'Correcting...' : 'Reset Result'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
          <p className="text-amber-800 font-semibold mb-2">No bracket matchups yet</p>
          <p className="text-amber-700 mb-4">
            Generate bracket matchups to start entering results
          </p>
          <button
            onClick={handleGenerateMatchups}
            disabled={generating}
            className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? 'Generating...' : 'Generate Now'}
          </button>
        </div>
      )}
    </div>
  );
}

interface TeamSlotProps {
  team?: BracketTeam;
  score?: number;
  isWinner: boolean;
  isFinal: boolean;
  isEliminated: boolean;
  onClick?: () => void;
  isLoading: boolean;
}

function TeamSlot({
  team,
  score,
  isWinner,
  isFinal,
  isEliminated,
  onClick,
  isLoading,
}: TeamSlotProps) {
  const isTBD = !team || team.name.includes('TBD');

  return (
    <div
      onClick={onClick}
      className={`
        flex items-center justify-between gap-3 px-3 py-2.5 rounded border transition-colors
        ${onClick && !isLoading ? 'cursor-pointer' : ''}
        ${onClick && !isFinal && !isLoading ? 'hover:bg-blue-100' : ''}
        ${onClick && isFinal && isEliminated && !isLoading ? 'hover:bg-orange-50 hover:border-orange-300' : ''}
        ${isWinner ? 'bg-green-100 border-green-400 font-semibold' : 'border-gray-300 bg-white'}
        ${isEliminated ? 'bg-gray-200 text-gray-400 line-through' : ''}
        ${isLoading ? 'opacity-50 cursor-wait' : ''}
        ${isTBD ? 'bg-gray-100 text-gray-400 italic' : ''}
      `}
      title={isFinal && isEliminated ? 'Click to correct — make this team the winner instead' : undefined}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {team ? (
          <>
            <span className={`
              w-6 h-6 flex items-center justify-center rounded text-xs font-bold flex-shrink-0
              ${isTBD ? 'bg-gray-300 text-gray-500' : 'bg-blue-600 text-white'}
            `}>
              {team.seed || '?'}
            </span>
            <span className="truncate text-sm">
              {isTBD ? 'TBD' : team.name}
            </span>
          </>
        ) : (
          <span className="text-gray-400 italic text-sm">TBD</span>
        )}
      </div>
      {isFinal && score !== undefined && (
        <span className={`text-lg font-bold flex-shrink-0 ${isWinner ? 'text-green-700' : 'text-gray-400'}`}>
          {score}
        </span>
      )}
    </div>
  );
}

export default AdminBracket;
